<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">Shop stocktaking ({{ dateTimeFormat(stocktaking_data.created_at, false) }})</h1>
          <span class="status-tag" :style="{ 'backgroundColor': stocktaking_data.completed ? '#77b017' : '#8a8a8a' }">
            {{ stocktaking_data.completed ? 'Completed' : 'Draft' }}
          </span>
        </app-cells>
        <app-button v-if="!stocktaking_data.completed" @click.native="onShowModal('delete')" theme="error">Delete</app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Property:</span><span>{{ stocktaking_data.property.name }}</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Receptionist:</span><span>{{ stocktaking_data.created_by.full_name }}</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Created at:</span><span>{{ dateTimeFormat(stocktaking_data.created_at, true) }}</span>
          </div>
        </div>
      </div>
      <app-cells>
        <app-textarea
          v-model="stocktaking_data.note"
          rows="5"
          @change.native="onUpdateNotes"
          placeholder="Write a notes"
          :readonly="stocktaking_data.completed"
        />
      </app-cells>
      <app-cells><h2 class="title">Items</h2></app-cells>
      <v-client-table
        :data="stocktaking_data.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--40': is_hotel_reception }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #count="{ index }">{{ index }}</template>
        <template #name="{ row }"><div class="table-default__left">{{ row.name }}</div></template>
        <template #quantity_real="{ row, index }">
          <input
            v-model="stocktaking_data.items[index - 1].quantity_real"
            v-if="!stocktaking_data.completed"
            class="input input--200"
            style="text-align: center"
            type="number"
            min="1"
          />
          <span v-else>{{ row.quantity_real }}</span>
        </template>
      </v-client-table>
      <app-cells position="end">
        <app-button v-if="!stocktaking_data.completed" @click.native="onPrint" theme="warn" ref="print">
          Print stocktaking
        </app-button>
        <app-button
          v-if="!stocktaking_data.completed"
          @click.native="onDecideCompleteInventory"
        >
          Complete inventory
        </app-button>
      </app-cells>
    </template>
    <modal
      name="control-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('control-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          <span v-if="modal_type === 'delete'">
            Delete stocktaking
          </span>
          <span v-else>
            Complete inventory
          </span>
        </div>
        <div class="popup__body">
          <div class="popup__subt">
            <span v-if="modal_type === 'delete'">
              Are you sure you want to delete this stocktaking?
            </span>
            <span v-else>
              Are you sure you want to do a full inventory? The product balance will change and cannot be undone
            </span>
          </div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideModal" theme="dark">Cancel</app-button>
            <app-button v-if="modal_type === 'delete'" @click.native="onDeleteStocktaking" ref="load">Yes</app-button>
            <app-button v-else @click.native="onCompleteInventory" ref="load">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getHotelManagementShopStocktaking,
  patchHotelManagementShopStocktaking,
  deleteHotelManagementShopStocktaking,
  getHotelManagementShopStocktakingExport
} from '@/http'
import { format, parseISO } from 'date-fns'

export default {
  name: 'HotelManagementShopStocktakingDetail',
  data() {
    return {
      page_loaded: false,
      stocktaking_data: {
        items: [],
      },
      table: {
        items: [],
        columns: ['count', 'name', 'quantity_real'],
        options: {
          headings: {
            'count': '#',
            'name': 'Name',
            'quantity_real': 'Real qty',
            'quantity': 'Declared qty',
            'reorder_threshold': 'Reorder threshold',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      modal_type: null,
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_qty_filled() {
      return this.stocktaking_data.items.every(item => item.quantity_real)
    },
    is_hotel_reception() {
      return this.$store.state.userGroups.map(item => item.code).includes('hotel_reception')
    },
  },
  created() {
    if (this.is_general_manager) this.table.columns = ['count', 'name', 'quantity_real', 'quantity', 'reorder_threshold']
    this.fetchData()
  },
  methods: {
    fetchData() {
      getHotelManagementShopStocktaking(Number(this.$route.params.id))
        .then(response => {
          this.stocktaking_data = response.data
          this.page_loaded = true
        })
    },
    onUpdateNotes() {
      patchHotelManagementShopStocktaking(Number(this.$route.params.id), { note: this.stocktaking_data.note })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Notes saved',
          })
        })
        .catch(() => { this.fetchData() })
    },
    onDecideCompleteInventory() {
      if (this.is_qty_filled) {
        this.onShowModal('complete')
      } else {
        this.$notify({
          type: 'error',
          title: 'Fill all the fields &quot;Real qty&quot;',
        })
      }
    },
    onShowModal(type) {
      this.modal_type = type
      this.$modal.show('control-modal')
    },
    onHideModal() {
      this.$modal.hide('control-modal')
    },
    onDeleteStocktaking() {
      this.$refs.load.preload = true
      deleteHotelManagementShopStocktaking(Number(this.$route.params.id))
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Stocktaking deleted',
          })
          this.$router.push({ name: 'hotel-management-shop-stocktaking' })
        })
        .catch(() => { this.$refs.load.preload = false })
    },
    onCompleteInventory() {
      this.$refs.load.preload = true
      const data = this.stocktaking_data.items.map(item => {
        return {
          id: item.id,
          quantity_real: item.quantity_real
        }
      })
      patchHotelManagementShopStocktaking(Number(this.$route.params.id), {items: data})
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Stocktaking completed',
          })
          this.$router.push({ name: 'hotel-management-shop-stocktaking' })
        })
        .catch(() => { this.$refs.load.preload = false })
    },
    onPrint() {
      this.$refs.print.preload = true
      getHotelManagementShopStocktakingExport(Number(this.$route.params.id))
        .then(response => {
          this.$refs.print.preload = false
          window.open(response.data)
        })
        .catch(() => { this.$refs.print.preload = false })
    },
    dateTimeFormat(date, full_string) {
      if (full_string) return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
      else return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
  }
}
</script>
